/**
 * Messages Page for trAInedup.ai
 * Direct messaging interface with conversation list and chat view
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Search,
  Plus,
  MoreVertical,
  Paperclip,
  Smile,
  Phone,
  Video,
  Info,
  Check,
  CheckCheck,
  Circle,
} from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { useAuth } from '../contexts/AuthContext';
import { communityService } from '../services/firebase/community';
import { Conversation, Message, MessageType } from '../types/community';

/**
 * Messages page component
 * Provides direct messaging functionality with real-time updates
 */
export const MessagesPage: React.FC = () => {
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_showNewConversation, setShowNewConversation] = useState(false);

  /**
   * Load user's conversations
   */
  useEffect(() => {
    if (!user) return;

    const loadConversations = async () => {
      try {
        setLoading(true);
        const conversationsData = await communityService.getUserConversations(user.uid);
        setConversations(conversationsData);
        
        // Select first conversation if none selected
        if (conversationsData.length > 0 && !selectedConversation) {
          setSelectedConversation(conversationsData[0]);
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();

    // Subscribe to real-time conversation updates
    const unsubscribe = communityService.subscribeToUserConversations(
      user.uid,
      (updatedConversations) => {
        setConversations(updatedConversations);
      }
    );

    return () => unsubscribe();
  }, [user, selectedConversation]);

  /**
   * Load messages for selected conversation
   */
  useEffect(() => {
    if (!selectedConversation) return;

    const loadMessages = async () => {
      try {
        const { messages: messagesData } = await communityService.getConversationMessages(
          selectedConversation.id,
          50
        );
        setMessages(messagesData);
        scrollToBottom();
      } catch (error) {
        console.error('Error loading messages:', error);
      }
    };

    loadMessages();

    // Subscribe to real-time message updates
    const unsubscribe = communityService.subscribeToConversationMessages(
      selectedConversation.id,
      (updatedMessages) => {
        setMessages(updatedMessages);
        scrollToBottom();
      }
    );

    return () => unsubscribe();
  }, [selectedConversation]);

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  /**
   * Send a message
   */
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user) return;

    try {
      setSending(true);
      
      await communityService.sendMessage({
        conversationId: selectedConversation.id,
        type: MessageType.TEXT,
        content: newMessage.trim(),
        attachments: [],
        mentions: [],
        isEdited: false,
        isDeleted: false,
        readBy: [],
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  /**
   * Handle key press in message input
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Format message time
   */
  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  /**
   * Get conversation display name
   */
  const getConversationDisplayName = (conversation: Conversation) => {
    if (conversation.name) return conversation.name;
    
    // For direct messages, show the other participant's name
    const otherParticipant = conversation.participants.find(id => id !== user?.uid);
    return otherParticipant ? `User ${otherParticipant.slice(-4)}` : 'Unknown User';
  };

  /**
   * Get message status icon
   */
  const getMessageStatusIcon = (message: Message) => {
    if (!user || message.senderId !== user.uid) return null;
    
    const readByOthers = message.readBy.filter(read => read.userId !== user.uid);
    
    if (readByOthers.length > 0) {
      return <CheckCheck className="w-4 h-4 text-blue-500" />;
    } else {
      return <Check className="w-4 h-4 text-neutral-400" />;
    }
  };

  /**
   * Conversation item component
   */
  const ConversationItem: React.FC<{ conversation: Conversation }> = ({ conversation }) => {
    const isSelected = selectedConversation?.id === conversation.id;
    const displayName = getConversationDisplayName(conversation);
    
    return (
      <div
        onClick={() => setSelectedConversation(conversation)}
        className={`p-4 cursor-pointer border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${
          isSelected ? 'bg-primary-50 border-primary-200' : ''
        }`}
      >
        <div className="flex items-center space-x-3">
          {/* Avatar */}
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary font-medium">
              {displayName.charAt(0).toUpperCase()}
            </span>
          </div>
          
          {/* Conversation Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-medium text-neutral-800 truncate">
                {displayName}
              </h3>
              {conversation.lastMessageAt && (
                <span className="text-xs text-neutral-500">
                  {formatMessageTime(conversation.lastMessageAt)}
                </span>
              )}
            </div>
            
            {conversation.lastMessagePreview && (
              <p className="text-sm text-neutral-600 truncate">
                {conversation.lastMessagePreview}
              </p>
            )}
          </div>
          
          {/* Unread indicator */}
          <div className="w-2 h-2 bg-primary rounded-full opacity-0"></div>
        </div>
      </div>
    );
  };

  /**
   * Message bubble component
   */
  const MessageBubble: React.FC<{ message: Message }> = ({ message }) => {
    const isOwn = message.senderId === user?.uid;
    
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-2' : 'order-1'}`}>
          {/* Message bubble */}
          <div
            className={`px-4 py-2 rounded-2xl ${
              isOwn
                ? 'bg-primary text-white rounded-br-md'
                : 'bg-neutral-100 text-neutral-800 rounded-bl-md'
            }`}
          >
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
          
          {/* Message info */}
          <div className={`flex items-center mt-1 space-x-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            <span className="text-xs text-neutral-500">
              {formatMessageTime(message.createdAt)}
            </span>
            {getMessageStatusIcon(message)}
          </div>
        </div>
        
        {/* Avatar for received messages */}
        {!isOwn && (
          <div className="w-8 h-8 bg-neutral-200 rounded-full flex items-center justify-center mr-2 order-0">
            <span className="text-xs text-neutral-600">
              {message.senderId.slice(-2).toUpperCase()}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex h-full bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
            {/* Conversations Sidebar */}
            <div className="w-80 border-r border-neutral-200 flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-neutral-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-lg text-neutral-800">Messages</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Plus}
                    onClick={() => setShowNewConversation(true)}
                  >
                    New
                  </Button>
                </div>
                
                <Input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                  inputSize="sm"
                />
              </div>
              
              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {conversations.length > 0 ? (
                  conversations
                    .filter(conv => 
                      getConversationDisplayName(conv)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    )
                    .map(conversation => (
                      <ConversationItem key={conversation.id} conversation={conversation} />
                    ))
                ) : (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-neutral-400" />
                    </div>
                    <h3 className="font-medium text-neutral-800 mb-2">No conversations yet</h3>
                    <p className="text-sm text-neutral-600 mb-4">
                      Start a conversation with someone from the community
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={Plus}
                      onClick={() => setShowNewConversation(true)}
                    >
                      Start Conversation
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-neutral-200 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary font-medium">
                            {getConversationDisplayName(selectedConversation).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-neutral-800">
                            {getConversationDisplayName(selectedConversation)}
                          </h3>
                          <div className="flex items-center text-sm text-neutral-500">
                            <Circle className="w-2 h-2 text-green-500 mr-1" />
                            Online
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" icon={Phone} />
                        <Button variant="ghost" size="sm" icon={Video} />
                        <Button variant="ghost" size="sm" icon={Info} />
                        <Button variant="ghost" size="sm" icon={MoreVertical} />
                      </div>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 bg-neutral-50">
                    {messages.length > 0 ? (
                      <>
                        {messages.map(message => (
                          <MessageBubble key={message.id} message={message} />
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Send className="w-8 h-8 text-neutral-400" />
                          </div>
                          <h3 className="font-medium text-neutral-800 mb-2">Start the conversation</h3>
                          <p className="text-sm text-neutral-600">
                            Send a message to begin chatting
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-neutral-200 bg-white">
                    <div className="flex items-end space-x-3">
                      <Button variant="ghost" size="sm" icon={Paperclip} />
                      
                      <div className="flex-1">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Type a message..."
                          className="w-full resize-none border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                          rows={1}
                          style={{ minHeight: '40px', maxHeight: '120px' }}
                        />
                      </div>
                      
                      <Button variant="ghost" size="sm" icon={Smile} />
                      
                      <Button
                        variant="primary"
                        size="sm"
                        icon={Send}
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sending}
                        loading={sending}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full bg-neutral-50">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-10 h-10 text-neutral-400" />
                    </div>
                    <h3 className="font-semibold text-xl text-neutral-800 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-neutral-600 mb-6">
                      Choose a conversation from the sidebar to start messaging
                    </p>
                    <Button
                      variant="primary"
                      icon={Plus}
                      onClick={() => setShowNewConversation(true)}
                    >
                      Start New Conversation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

